import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Crown, CreditCard, Sparkle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { openCheckout } from '@/lib/razorpay';
import { useAuth } from '@/context/AuthContext';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function BillingPage() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState({ enabled: false });
  const [period, setPeriod] = useState('month');
  const [paying, setPaying] = useState('');
  const isAdmin = user?.role === 'admin';

  const load = () => Promise.all([
    api.get('/auth/me/company'), api.get('/plans'), api.get('/payments/subscription/history'), api.get('/payments/config'),
  ]).then(([c, p, h, cfg]) => { setCompany(c.data); setPlans(p.data || []); setHistory(h.data || []); setConfig(cfg.data); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const pay = async (plan) => {
    setPaying(plan.id);
    try {
      const { data: order } = await api.post('/payments/subscription/order', { plan_id: plan.id, period });
      const res = await openCheckout({ ...order, name: 'SMSuitePro', description: `${order.plan_name} plan · ${period === 'year' ? 'Yearly' : 'Monthly'}` });
      const { data } = await api.post('/payments/subscription/verify', res);
      toast.success(`${plan.name} plan activated till ${fmtDate(data.plan_expires_at)}`);
      await load();
      window.dispatchEvent(new CustomEvent('company-updated'));
    } catch (err) {
      const msg = err?.response ? formatApiError(err.response?.data?.detail) : err.message;
      if (msg !== 'Payment cancelled') toast.error(msg || 'Payment failed');
    } finally {
      setPaying('');
    }
  };

  if (!company) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  const expired = company.trial_expired || company.plan_expired;
  const onTrial = company.subscription_plan === 'basic' && company.trial_ends_at;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="billing-page">
      <Card className={`rounded-2xl border ${expired ? 'border-red-200 bg-red-50/60' : 'border-[#E4E4E7]'}`} data-testid="billing-current-plan">
        <CardContent className="p-5 flex flex-wrap items-center gap-5">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${expired ? 'bg-red-100 text-red-600' : 'bg-[#E0E7FF] text-[#4F46E5]'}`}>
            {expired ? <Warning size={24} weight="fill" /> : <Crown size={24} weight="fill" />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA]">Current plan</p>
            <p className="text-xl font-bold capitalize" data-testid="billing-plan-name">{company.subscription_plan} {company.plan_period ? <span className="text-sm font-medium text-[#52525B]">· {company.plan_period}</span> : null}</p>
            <p className="text-sm text-[#52525B] mt-0.5" data-testid="billing-plan-status">
              {expired
                ? (onTrial ? 'Your free trial has ended. Choose a plan below to continue.' : `Your plan expired on ${fmtDate(company.plan_expires_at)}. Renew below to continue.`)
                : onTrial
                  ? `Free trial · ${company.trial_remaining_days ?? 0} day${company.trial_remaining_days === 1 ? '' : 's'} left (ends ${fmtDate(company.trial_ends_at)})`
                  : company.plan_expires_at ? `Active · renews / expires on ${fmtDate(company.plan_expires_at)} (${company.plan_remaining_days} days left)` : 'Active'}
            </p>
          </div>
          <div className="inline-flex rounded-full border border-[#E4E4E7] bg-white p-0.5" data-testid="billing-period-toggle">
            {['month', 'year'].map(p => (
              <button key={p} type="button" onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${period === p ? 'bg-[#1E3A8A] text-white' : 'text-[#52525B] hover:text-[#0A0A0A]'}`}
                data-testid={`billing-period-${p}`}>{p === 'month' ? 'Monthly' : 'Yearly'}</button>
            ))}
          </div>
        </CardContent>
      </Card>

      {!config.enabled && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="billing-disabled-note">
          Online payments are not configured yet. Contact support to activate your plan.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.map(plan => {
          const monthly = plan.price || 0;
          const amount = period === 'year' ? (plan.yearly_price || monthly * 12) : monthly;
          const current = company.subscription_plan === plan.id && !expired && !onTrial;
          const highlight = plan.id === 'pro';
          return (
            <Card key={plan.id} className={`rounded-2xl border ${highlight ? 'border-[#1E3A8A] shadow-md' : 'border-[#E4E4E7]'}`} data-testid={`billing-plan-${plan.id}`}>
              <CardContent className="p-5 flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <p className="text-base font-bold">{plan.name}</p>
                  {highlight && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] rounded-full px-2 py-0.5"><Sparkle size={11} weight="fill" /> Popular</span>}
                </div>
                <p className="mt-2"><span className="text-3xl font-bold">₹{amount.toLocaleString('en-IN')}</span><span className="text-sm text-[#52525B]"> / {period}</span></p>
                {period === 'year' && plan.yearly_price && monthly ? <p className="text-[11px] text-emerald-600 font-semibold">Save ₹{(monthly * 12 - plan.yearly_price).toLocaleString('en-IN')} vs monthly</p> : null}
                <ul className="mt-4 space-y-1.5 flex-1">
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#3F3F46]"><CheckCircle size={15} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button
                  onClick={() => pay(plan)}
                  disabled={!isAdmin || !config.enabled || !!paying || amount <= 0}
                  className={`mt-5 w-full rounded-xl ${highlight ? 'bg-[#1E3A8A] hover:bg-[#1E40AF] text-white' : 'bg-[#0A0A0A] hover:bg-[#27272A] text-white'}`}
                  data-testid={`billing-pay-${plan.id}`}
                >
                  <CreditCard size={16} className="mr-1.5" />
                  {paying === plan.id ? 'Opening Razorpay…' : current ? `Renew ${plan.name}` : expired || onTrial ? `Activate ${plan.name}` : `Switch to ${plan.name}`}
                </Button>
                {!isAdmin && <p className="text-[10px] text-[#A1A1AA] mt-1 text-center">Only the company admin can pay.</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="rounded-2xl border-[#E4E4E7]" data-testid="billing-history">
        <CardContent className="p-5">
          <p className="text-sm font-semibold mb-3">Payment history</p>
          {history.length === 0 ? (
            <p className="text-xs text-[#A1A1AA]">No payments yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-[#A1A1AA] text-left border-b border-[#F4F4F5]">
                <th className="py-1.5">Date</th><th>Plan</th><th>Period</th><th className="text-right">Amount</th><th>Payment ID</th><th>Valid till</th>
              </tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.order_id} className="border-b border-[#F4F4F5] last:border-0" data-testid={`billing-history-row-${h.order_id}`}>
                    <td className="py-2">{fmtDate(h.paid_at)}</td>
                    <td className="font-medium">{h.plan_name || h.plan_id}</td>
                    <td className="capitalize">{h.period === 'year' ? 'Yearly' : 'Monthly'}</td>
                    <td className="text-right font-mono">₹{(h.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="font-mono text-xs text-[#52525B]">{h.payment_id}</td>
                    <td>{fmtDate(h.plan_expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
